import type { ReactNode } from 'react';
import { CheckIcon, PlusCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

type DataTableFacetedFilterProps = {
    title: string;
    options: { value: string; label: string }[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    multi?: boolean;
    renderOption?: (option: { value: string; label: string }) => ReactNode;
};

export function DataTableFacetedFilter({
    title,
    options,
    selectedValues,
    onChange,
    multi = false,
    renderOption,
}: DataTableFacetedFilterProps) {
    const handleSelect = (value: string) => {
        if (multi) {
            if (selectedValues.includes(value)) {
                onChange(selectedValues.filter((v) => v !== value));
            } else {
                onChange([...selectedValues, value]);
            }
        } else {
            if (selectedValues.includes(value)) {
                onChange([]);
            } else {
                onChange([value]);
            }
        }
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-8 border-dashed bg-filter', selectedValues.length > 0 && 'bg-filter-active')}
                >
                    <PlusCircle className="size-3.5" />
                    {title}
                    {selectedValues.length > 0 && (
                        <>
                            <Separator orientation="vertical" className="mx-1" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                                {selectedValues.length}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                                {selectedValues.length > 2 ? (
                                    <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                        {selectedValues.length} sélectionnés
                                    </Badge>
                                ) : (
                                    selectedValues.map((value) => {
                                        const option = options.find((o) => o.value === value);
                                        return renderOption && option ? (
                                            <span key={value}>{renderOption(option)}</span>
                                        ) : (
                                            <Badge
                                                key={value}
                                                variant="secondary"
                                                className="rounded-sm px-1 font-normal"
                                            >
                                                <span>{option?.label || value}</span>
                                            </Badge>
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0" align="start">
                <Command>
                    <CommandInput placeholder={`Rechercher ${title.toLowerCase()}...`} />
                    <CommandList>
                        <CommandEmpty>Aucun résultat.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = selectedValues.includes(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => handleSelect(option.value)}
                                        data-checked={isSelected}
                                    >
                                        <div
                                            className={cn(
                                                'flex size-4 items-center justify-center rounded-sm border border-primary',
                                                isSelected
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'opacity-50 [&_svg]:invisible',
                                            )}
                                        >
                                            <CheckIcon className="size-3" />
                                        </div>
                                        {renderOption ? renderOption(option) : <span>{option.label}</span>}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                    {selectedValues.length > 0 && (
                        <>
                            <Separator />
                            <CommandGroup>
                                <CommandItem className="justify-center text-center" onSelect={() => onChange([])}>
                                    Effacer les filtres
                                </CommandItem>
                            </CommandGroup>
                        </>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
